/** Flies the Osprey model according to the sim's phase: inbound, hovering over the X, away. */

import { type Material, type Scene, Vector3 } from 'three';
import { OSPREY } from '../../sim/tuning';
import { type MissionDef, TICK_RATE, type World } from '../../sim/types';
import type { PixelCamera } from '../camera/PixelCamera';
import { buildOsprey, type OspreyModel } from '../kit/vehicles';
import type { Palette } from '../palette';
import { castsFrom, setCastShadow } from './aircraftShadow';

/** Hover height over the pickup, metres: the length of the rope ladder, and how far a man climbs. */
export const HOVER_HEIGHT = 7;
/** Where it comes from and goes to, relative to the pickup: far off to the south-east, high. */
const APPROACH = new Vector3(55, 22, 85);
const DEPARTURE = new Vector3(-30, 30, -120);

export class OspreyView {
  private readonly model: OspreyModel;
  private readonly position = new Vector3();
  private readonly snapped = new Vector3();
  private departedFor = 0;

  constructor(scene: Scene, palette: Palette, material: Material, _def: MissionDef, private readonly groundY: (x: number, z: number) => number) {
    this.model = buildOsprey(palette, material);
    this.model.root.visible = false;
    scene.add(this.model.root);
  }

  update(world: World, time: number, dt: number, camera: PixelCamera): void {
    const o = world.osprey;
    const root = this.model.root;
    if (o.phase === 'away') {
      root.visible = false;
      return;
    }
    const px = world.objective.pickupX;
    const pz = world.objective.pickupZ;
    const ground = this.groundY(px, pz);
    // The ladder hangs from the rear ramp, so the aircraft sits a little north of the X.
    const hoverZ = pz - 6.95;
    root.visible = true;
    let height = HOVER_HEIGHT;

    if (o.phase === 'inbound') {
      const total = o.recalled ? OSPREY.inboundTicks : 1;
      const t = 1 - Math.min(1, o.ticks / total);
      // Audible the whole way in, visible for the last few seconds: ease in over the final 30 %.
      const k = Math.max(0, (t - 0.7) / 0.3);
      const ease = k * k * (3 - 2 * k);
      this.position.set(px + APPROACH.x * (1 - ease), ground + HOVER_HEIGHT + APPROACH.y * (1 - ease), hoverZ + APPROACH.z * (1 - ease));
      this.model.setLadder(false, HOVER_HEIGHT);
      this.departedFor = 0;
      height = HOVER_HEIGHT + APPROACH.y * (1 - ease);
      // The comment above has always said "visible for the last few seconds", and it was visible
      // for all of them: a hundred metres out and forty up, hanging at the edge of the world until
      // it swooped. It appears when the approach actually begins.
      root.visible = ease > 0;
    } else if (o.phase === 'hovering') {
      const bob = Math.sin(time * 1.7) * 0.18;
      this.position.set(px, ground + HOVER_HEIGHT + bob, hoverZ);
      this.model.setLadder(true, HOVER_HEIGHT + bob);
      height = HOVER_HEIGHT + bob;
    } else {
      this.departedFor += dt;
      const k = Math.min(1, this.departedFor / (TICK_RATE / 10));
      const ease = k * k;
      this.position.set(px + DEPARTURE.x * ease, ground + HOVER_HEIGHT + DEPARTURE.y * ease, hoverZ + DEPARTURE.z * ease);
      this.model.setLadder(false, HOVER_HEIGHT);
      height = HOVER_HEIGHT + DEPARTURE.y * ease;
      if (k >= 1) root.visible = false;
    }
    setCastShadow(root, castsFrom(height));
    camera.snapToTexel(this.position, this.snapped);
    root.position.copy(this.snapped);
    this.model.update(time, dt);
  }
}
